import { FC, useCallback, useRef, useState } from 'react';
import { Center } from '@chakra-ui/react';
import { without } from 'ramda';

import Button from 'components/_core/Button';
import Text from 'components/_core/Text';
import Row from 'components/Row';
import Header from 'features/Onboarding/components/Header';
import Page from 'features/Onboarding/legacy/components/Page';
import { OnboardingPageContent } from 'features/Onboarding/pageLayoutStyles';

import BeneficiaryCard from './BeneficiaryCard';
import locales from './locales';
import NewBeneficiaryCard from './NewBeneficiaryCard';
import {
  BeneficiariesDocsComponentProps,
  CompanyLegalBeneficiary,
  CompanyLegalBeneficiaryStatus,
} from './types';

const WAITING_BENEFICIARY_STATUSES: CompanyLegalBeneficiaryStatus[] = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'INVALID',
];

const countPending = (list: CompanyLegalBeneficiary[]): number =>
  list.filter(({ onboardingStatus }) =>
    WAITING_BENEFICIARY_STATUSES.includes(onboardingStatus),
  ).length;

const BeneficiariesDocsComponent: FC<BeneficiariesDocsComponentProps> = ({
  beneficiaries,
  companyProfileId,
  hideAddOthersOption,
  loading,
  onGoToHome,
}) => {
  const lastNewBeneficiaryKey = useRef(-1);
  const pendingBeneficiaries = beneficiaries ? countPending(beneficiaries) : 0;

  // A list to keep track of new beneficiary cards using indexes
  const [newBeneficiariesCardIndexes, setNewBeneficiariesCardIndexes] =
    useState<number[]>([]);

  const createBeneficiary = useCallback(() => {
    lastNewBeneficiaryKey.current += 1;
    setNewBeneficiariesCardIndexes([
      ...newBeneficiariesCardIndexes,
      lastNewBeneficiaryKey.current,
    ]);
  }, [newBeneficiariesCardIndexes]);

  // create a new array from slices excluding keyIndex
  const onRemoveCard = (key: number) => {
    setNewBeneficiariesCardIndexes(without([key], newBeneficiariesCardIndexes));
  };

  return (
    <Page backButtonNavigatesBack loading={loading}>
      <OnboardingPageContent>
        <Header
          description={
            pendingBeneficiaries === 0
              ? locales.descriptionAllComplete
              : locales.descriptionWaiting
          }
          title={locales.title}
        />

        <Row alignItems="center" justifyContent="space-between">
          <Text fontWeight="weight-500" size="lg" variant="primary">
            {locales.subTitle}
          </Text>
          {hideAddOthersOption ? null : (
            <Button
              icon="plus"
              iconPosition="left"
              onClick={createBeneficiary}
              variant="inline-primary"
            >
              {locales.addCTA}
            </Button>
          )}
        </Row>

        {beneficiaries?.map((beneficiary) => {
          if (beneficiary.invitation === null) {
            throw new Error(
              'Beneficiary unexpectedly missing the invitation field',
            );
          }
          return (
            <BeneficiaryCard
              beneficiary={beneficiary}
              companyProfileId={companyProfileId}
              key={beneficiary.companyLegalBeneficiaryId}
            />
          );
        })}

        {newBeneficiariesCardIndexes.map((key) => (
          <NewBeneficiaryCard
            companyProfileId={companyProfileId}
            key={key}
            removeCard={() => onRemoveCard(key as number)}
          />
        ))}

        <Center marginTop="space-16">
          <Button
            icon="arrow-left"
            iconPosition="left"
            marginX="auto"
            onClick={onGoToHome}
            variant="secondary"
          >
            {locales.goToHome}
          </Button>
        </Center>
      </OnboardingPageContent>
    </Page>
  );
};

export default BeneficiariesDocsComponent;
